import { Box } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { QUERIES } from 'components/pages/explore/query/Query'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppEntitlementValueWithOneOwner } from 'lib/actions/apps/app-entitlement'
import { SearchAppEntitlementsWithOneOwnerRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchEntitlementsWithOneOwner } from 'redux_toolkit/entitlements'
import { ExportData } from './ExportData'
import { StructuredSearchStyles } from './styles'

const cols: Array<C1TableReduxColumn<AppEntitlementValueWithOneOwner>> = [
    {
        field: 'name',
        label: <FormattedMessage id="components_entitlement_with_one_owner_entitlement_name" defaultMessage="Entitlement name" />,
        maxWidth: '300px',
        Component: ({ row }) => <Box sx={{ overflow: 'hidden' }}>
            <C1EntitlementDisplayName entitlement={row.entitlement} />
        </Box>,
    },
    {
        field: 'app',
        label: <FormattedMessage id="components_entitlement_with_one_owner_list_app" defaultMessage="App" />,
        Component: ({ row }) => <Box>
            {row.entitlement?.app ? (
                <AppDisplayName app={row.entitlement.app} avatarSize="xs" />
            ) : (
                <C1EmptyIndicator />
            )}
        </Box>,
    },
    {
        field: 'owner',
        label: <FormattedMessage id="components_entitlement_with_one_owner_list_owner" defaultMessage="Owner" />,
        Component: ({ row }) => <Box>
            <UserDisplayName user={row.user} />
        </Box>,
    },
]

const exportDataActionRequest = ExportDataRequest.create({
    queryId: QueryId.APP_ENTITLEMENTS_WITH_ONE_OWNER,
    request: {
        oneofKind: 'searchAppEntitlementsWithOneOwnerRequest',
        searchAppEntitlementsWithOneOwnerRequest:
                SearchAppEntitlementsWithOneOwnerRequest.create({ expandMask: { paths: ['*'] } }),
    },
})

export const AppEntitlementsWithOneOwnerReport: FC = () => {
    const [page, setPage] = useState<number>(0)
    const { data,
        isError,
        isLoading,
        error,
        canLoadMore,
        getPage } = searchEntitlementsWithOneOwner.useSearchAppEntitlementsWithOneOwner(
        SearchAppEntitlementsWithOneOwnerRequest.create({ expandMask: { paths: ['*'] } }),
        {},
    )
    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AppEntitlementsWithOneOwner)

    const table = useMemo(
        () => (
            <C1TableRedux
                data={data}
                cols={cols}
                isError={isError}
                isLoading={isLoading}
                error={error}
                page={page}
                canLoadMore={canLoadMore}
                onPageChange={onPageChange}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    return <>
        <Box sx={StructuredSearchStyles.exportDataBox}>
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Box>
        <List<AppEntitlementValueWithOneOwner> table={table} />
    </>
}
